import type { CSSProperties, MouseEvent } from 'react';

import BpkPrice, { SIZES } from '@skyscanner/backpack-web/bpk-component-price';
import BpkText, {
  TEXT_COLORS,
  TEXT_STYLES,
} from '@skyscanner/backpack-web/bpk-component-text';
import { cssModules } from '@skyscanner/backpack-web/bpk-react-utils';

import { withI18n } from '../../contexts/i18n';
import Distance from '../Distance';
import DistancePOI from '../DistancePOI';
import HotelStars from '../HotelStars';
import ReviewRating, { SPACING_VARIANTS } from '../ReviewRating';

import CardImage from './CardImage';
import MiniCardLayout from './MiniCardLayout/MiniCardLayout';

import type { HotelCard } from '@shared/types/frontend/hotel-card';
import type { I18nService } from '@shared/types/i18n';
import type { Nullable } from '@shared/types/utils';

import STYLES from './MiniCard.module.scss';

import { CARD_AREA } from '@shared/constants/card-area';
import {
  getPriceTypeText,
  PRICE_TYPE_PER_NIGHT,
} from '@shared/utils-universal/price-type';

type MiniCardProps = {
  i18n: I18nService;
  hotel: HotelCard;
  position: number;
  showPrice?: boolean;
  onClick?: (params: any) => void;
  isVertical?: boolean;
  priceType?: Nullable<string>;
  className?: string;
  style?: CSSProperties;
  onHover?: (hotelId: string) => void;
  selected?: boolean;
};

const cls = cssModules(STYLES);

const MiniCard = ({
  className,
  hotel,
  i18n,
  isVertical,
  onClick,
  onHover,
  position,
  priceType,
  selected,
  showPrice,
  style,
}: MiniCardProps) => {
  const {
    distanceInfo,
    id: hotelId,
    image,
    name: hotelName,
    numberOfStars,
    priceInfo,
    relevantPOI,
    reviewsSummaryInfo,
    url,
  } = hotel;

  const cug = priceInfo?.cug;
  const originalPrice = cug?.originalPrice;

  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    onClick?.({
      id: hotelId, // for mini card on Dayview desktop map
      position,
      isMainPrice: true,
      isGoToHD: true,
      partnerId: priceInfo?.partnerId,
      price: priceInfo?.price,
      basePrice: priceInfo?.basePrice,
      taxesAndFees: priceInfo?.taxesAndFees,
      cug: cug ?? null,
      hasCoupon: false,
      isCoupon: false,
      mainPriceArea: CARD_AREA.primary,
      stars: numberOfStars,
    });
  };

  const imageNode = <CardImage image={image} name={hotelName} />;

  const hotelNameNode = (
    <BpkText textStyle={TEXT_STYLES.label1}>{hotelName}</BpkText>
  );

  const starsNode =
    numberOfStars! > 0 ? (
      <HotelStars
        stars={numberOfStars}
        className={cls(
          'MiniCard__stars',
          isVertical && 'MiniCard__stars--vertical',
        )}
      />
    ) : null;

  const reviewRatingNode = reviewsSummaryInfo?.reviewSummaryScore ? (
    <ReviewRating
      score={reviewsSummaryInfo.reviewSummaryScore}
      count={reviewsSummaryInfo.reviewSummaryCount}
      image={reviewsSummaryInfo.reviewSummaryScoreImageUrl}
      spacingVariant={
        showPrice ? SPACING_VARIANTS.FLUSH : SPACING_VARIANTS.NO_PADDING
      }
      onlyShowCount
    />
  ) : null;

  const distanceVariantNode = distanceInfo ? (
    <BpkText
      tagName="p"
      className={cls('MiniCard__distanceVariant')}
      color={TEXT_COLORS.textPrimary}
    >
      <Distance distance={distanceInfo} />
    </BpkText>
  ) : null;

  const distancePOINode = relevantPOI ? (
    <BpkText
      tagName="p"
      className={cls('MiniCard__distanceVariant')}
      color={TEXT_COLORS.textPrimary}
    >
      <DistancePOI relevantPOI={relevantPOI} />
    </BpkText>
  ) : (
    distanceVariantNode
  );

  const priceInfoNode = priceInfo?.price ? (
    <BpkPrice
      price={i18n.formatCurrency(priceInfo.price)}
      size={SIZES.small}
      previousPrice={originalPrice ? i18n.formatCurrency(originalPrice) : null}
      trailingText={getPriceTypeText(i18n, priceType ?? PRICE_TYPE_PER_NIGHT)}
    />
  ) : null;

  return (
    <MiniCardLayout
      url={url}
      className={className}
      style={style}
      selected={selected}
      onClick={handleClick}
      onHover={onHover ? () => onHover(hotelId) : undefined}
      showPrice={showPrice}
      isVertical={isVertical}
      imageNode={imageNode}
      hotelNameNode={hotelNameNode}
      starsNode={starsNode}
      reviewRatingNode={reviewRatingNode}
      distanceOrPriceNode={showPrice ? priceInfoNode : distancePOINode}
    />
  );
};

export default withI18n(MiniCard);
